import { FormControl } from '../../../../../../widgets/formControl';
import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { AdvancedSelect } from '../../../../../../widgets/select';
import { T } from 'src/shared/helpers';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';
import { ComposerAddButton, ComposerIconButton, composerFieldDensity } from '../composerWorkspace';
import {
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator
} from './composerAttributesModels';
import { ComposerValidationErrorCode } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';

// Header and rows share one grid, so the column labels sit exactly above their selects.
const EXPRESSION_COLUMNS = 'minmax(0, 1fr) 72px minmax(0, 1fr) 28px';

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    ${composerFieldDensity}
`;

const Intro = styled.p`
    margin: 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 12px;
    line-height: 18px;
`;

const Expressions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ExpressionHeader = styled.div`
    display: grid;
    grid-template-columns: ${EXPRESSION_COLUMNS};
    gap: 6px;
    padding: 0 6px 2px 8px;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const ExpressionRow = styled.div`
    display: grid;
    grid-template-columns: ${EXPRESSION_COLUMNS};
    align-items: center;
    gap: 6px;
    padding: 6px 6px 6px 8px;
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
`;

const ExpressionConnector = styled.span`
    padding-left: 8px;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const RuleEmpty = styled.div`
    padding: 14px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border: 1px dashed var(--composer-line, #dce3eb);
    border-radius: 8px;
    font-size: 12px;
    line-height: 18px;
`;

const AddButton = styled(ComposerAddButton)`
    align-self: flex-start;
`;

interface ComposerLinksEditorProps {
    attributes: ComposerAttributeNode[];
    links: ComposerAttributeNodeLink[];
    logicalOperator?: ComposerLogicalOperator;
    selfAttributeID?: string;
    subjectLabel: string;
    validationErrors?: ReadonlyMap<string, string>;
    onChange: (links: ComposerAttributeNodeLink[], logicalOperator: ComposerLogicalOperator) => void;
}

const ComposerLinksEditor: FC<ComposerLinksEditorProps> = ({
    attributes,
    links,
    logicalOperator = ComposerLogicalOperator.AND,
    selfAttributeID,
    subjectLabel,
    validationErrors,
    onChange
}) => {
    const availableAttributes = attributes.filter(
        (attribute) => attribute.type === ComposerAttributeType.Attribute && attribute.id !== selfAttributeID && attribute.options.length > 0
    );
    const attributeOptions = availableAttributes.map((attribute) => ({ value: attribute.id, label: attribute.name }));
    const logicalOptions = [
        { value: ComposerLogicalOperator.AND, label: T._('All of the following expressions are true', 'Admin') },
        { value: ComposerLogicalOperator.OR, label: T._('Any of the following expressions is true', 'Admin') }
    ];
    // The column is narrow: the selected value shows only the symbol, the menu spells it out.
    const comparisonOptions = [
        { value: ComposerComparisonOperator.EQUAL, symbol: '=', label: T._('Equal', 'Admin') },
        { value: ComposerComparisonOperator.NOT_EQUAL, symbol: '≠', label: T._('Not Equal', 'Admin') }
    ];
    const connectorLabel = logicalOperator === ComposerLogicalOperator.OR ? T._('or', 'Admin') : T._('and', 'Admin');
    const hasLinkError = (index: number, ...codes: string[]) => codes.some((code) => validationErrors?.has(`${code}:${index}`));

    const updateLink = (index: number, nextLink: ComposerAttributeNodeLink) => {
        const nextLinks = links.map((link, linkIndex) => (linkIndex === index ? nextLink : link));
        onChange(nextLinks, logicalOperator);
    };

    // A new expression starts empty, so the placeholders ask for a conscious choice instead of
    // silently linking the first attribute; validation flags it until both selects are filled.
    const addLink = () => onChange([...links, { attributeID: '', optionID: '', operation: ComposerComparisonOperator.EQUAL }], logicalOperator);

    return (
        <Rules>
            <Intro>
                {T._(
                    `${subjectLabel} is shown in the configurator only when these expressions are met, for example when a given option of another attribute is selected. Otherwise it is hidden.`,
                    'Admin'
                )}
            </Intro>
            <FormControl label={T._('Apply if', 'Admin')}>
                <AdvancedSelect
                    options={logicalOptions}
                    value={logicalOptions.find((option) => option.value === logicalOperator)}
                    onChange={(option) => onChange(links, option?.value ?? ComposerLogicalOperator.AND)}
                    menuPosition='fixed'
                />
            </FormControl>
            {links.length > 0 ? (
                <Expressions>
                    <ExpressionHeader aria-hidden='true'>
                        <span>{T._('Attribute', 'Admin')}</span>
                        <span>{T._('Operation', 'Admin')}</span>
                        <span>{T._('Option', 'Admin')}</span>
                        <span />
                    </ExpressionHeader>
                    {links.map((link, index) => {
                        const linkedAttribute = availableAttributes.find((attribute) => attribute.id === link.attributeID);
                        const optionOptions = (linkedAttribute?.options ?? []).map((option) => ({ value: option.id, label: option.name }));
                        return (
                            <React.Fragment key={`${link.attributeID}_${link.optionID}_${index}`}>
                                {index > 0 && <ExpressionConnector>{connectorLabel}</ExpressionConnector>}
                                <ExpressionRow>
                                    <AdvancedSelect
                                        aria-label={T._('Attribute', 'Admin')}
                                        options={attributeOptions}
                                        value={attributeOptions.find((option) => option.value === link.attributeID) ?? null}
                                        isError={hasLinkError(
                                            index,
                                            ComposerValidationErrorCode.LINK_INCOMPLETE,
                                            ComposerValidationErrorCode.LINK_ATTRIBUTE_NOT_FOUND,
                                            ComposerValidationErrorCode.LINK_SELF_REFERENCE
                                        )}
                                        onChange={(option) => {
                                            const attribute = availableAttributes.find((candidate) => candidate.id === option?.value);
                                            updateLink(index, {
                                                ...link,
                                                attributeID: attribute?.id ?? '',
                                                optionID: ''
                                            });
                                        }}
                                        placeholder={T._('Select an attribute', 'Admin')}
                                        menuPosition='fixed'
                                    />
                                    <AdvancedSelect
                                        aria-label={T._('Operation', 'Admin')}
                                        options={comparisonOptions}
                                        value={comparisonOptions.find((option) => option.value === link.operation)}
                                        isError={hasLinkError(index, ComposerValidationErrorCode.LINK_OPERATION_INVALID)}
                                        onChange={(option) => updateLink(index, { ...link, operation: option?.value ?? ComposerComparisonOperator.EQUAL })}
                                        formatOptionLabel={(option, { context }) => (context === 'value' ? option.symbol : `${option.symbol}  ${option.label}`)}
                                        isSearchable={false}
                                        menuPosition='fixed'
                                    />
                                    <AdvancedSelect
                                        aria-label={T._('Option', 'Admin')}
                                        options={optionOptions}
                                        value={optionOptions.find((option) => option.value === link.optionID) ?? null}
                                        isError={hasLinkError(
                                            index,
                                            ComposerValidationErrorCode.LINK_INCOMPLETE,
                                            ComposerValidationErrorCode.LINK_OPTION_NOT_FOUND
                                        )}
                                        onChange={(option) => updateLink(index, { ...link, optionID: option?.value ?? '' })}
                                        placeholder={T._('Select an option', 'Admin')}
                                        menuPosition='fixed'
                                    />
                                    <ZTooltip title={T._('Remove expression', 'Admin')}>
                                        <ComposerIconButton
                                            $danger
                                            aria-label={T._('Remove expression', 'Admin')}
                                            onClick={() =>
                                                onChange(
                                                    links.filter((_, linkIndex) => linkIndex !== index),
                                                    logicalOperator
                                                )
                                            }
                                        >
                                            <Icon>
                                                <DeleteIcon />
                                            </Icon>
                                        </ComposerIconButton>
                                    </ZTooltip>
                                </ExpressionRow>
                            </React.Fragment>
                        );
                    })}
                </Expressions>
            ) : (
                <RuleEmpty>{T._(`No expressions yet: ${subjectLabel.toLowerCase()} is always shown.`, 'Admin')}</RuleEmpty>
            )}
            <AddButton disabled={availableAttributes.length === 0} onClick={addLink}>
                <PlusIcon />
                {T._('Add', 'Admin')}
            </AddButton>
        </Rules>
    );
};

export default ComposerLinksEditor;
